import { Avatar } from './Avatar';
import { useFittedNames } from './useFittedNames';
import { Icon } from './Icon';
import { FlagStrip } from './FlagStrip';
import { issuesSeverity } from '../bookings/domain';
import type { BookingDog, HealthIssue } from '../bookings/types';
import { byDeskPriority, type BookingFlag } from '../bookings/flags';

export interface DogCellProps {
  // Optional only because `person` is the other way to fill this cell — a
  // viewing has no dogs on file to pass. Every existing caller still passes it.
  dogs?: BookingDog[];
  ownerName?: string;
  issues?: HealthIssue[];
  // Booking flags (booking-flags story). When provided, the cell renders the
  // at-a-glance flag strip — per-dog flags beside each dog's name, household
  // flags after — and replaces the legacy single health-warn indicator (which
  // the flag set now subsumes). When absent, behaviour is unchanged for the
  // owners list and day-detail callers that still pass `issues`.
  flags?: BookingFlag[];
  // A short qualifier shown before the owner on the same line — the desk row's
  // breed at phone width, where the row's own breed column is folded away (see
  // 12-mobile.css). `display: none` above the phone tier, so the desktop cell is
  // unchanged and the text is never in the accessibility tree twice.
  meta?: string;
  // A CONTACT IN PLACE OF THE DOG PILE — one person avatar and their name where
  // the dogs would be. A viewing has no dogs on file: passing a fabricated one
  // would be a lie in the data, and a parallel cell would be a second
  // truncation rule to keep in step with this one. When given, `dogs` is
  // ignored and whatever the caller knows about the animals goes in `meta`.
  person?: { name: string };
  // A SECOND LINE THAT IS NOT THE OWNER — the desk row's breed, since the desk
  // now gives the household a column of its own. Only used when `ownerName` is
  // absent, so every existing caller renders exactly as it did.
  subline?: string;
  // HOW MANY SLOTS THE PILE MAY OCCUPY — faces and counter together, not faces
  // alone. At two: one dog is one face, two are two, and three or more are one
  // face and a "+N", so the pile is never wider than two whatever the booking
  // holds. The pile shrink-wraps otherwise, pushing the names right by 18px per
  // dog, which on a table is a ragged column of names. Omitted everywhere else,
  // where the pile is free to run.
  pileSlots?: number;

}

// Shared "list of dogs with owner underneath" cell — bookings, owners,
// day-detail all use it. Read-only: carries no click. Lifted verbatim
// from _prototype/components.jsx DogCell.
export function DogCell({
  dogs = [],
  ownerName,
  issues,
  flags,
  meta,
  person,
  subline,
  pileSlots,
}: DogCellProps) {
  // The faces actually drawn, and how many stand behind them. The counter takes
  // a slot of its own, so overflowing at N slots draws N-1 faces.
  const overflows = pileSlots !== undefined && dogs.length > pileSlots;
  // AT ONE SLOT THE FACE BECOMES THE COUNT. A counter takes a slot of its own,
  // so a pile capped at one still drew two boxes and cost the 20px the cap was
  // meant to save — and dropping the counter altogether lost the fact that
  // there was more than one dog at all. One box, carrying the number, says it
  // in the space of a single avatar (Sir, 2026-08-27).
  const counting = pileSlots === 1 && dogs.length > 1;
  const shown = counting ? [] : overflows ? dogs.slice(0, Math.max(1, pileSlots! - 1)) : dogs;
  const hidden = counting ? dogs.length : dogs.length - shown.length;
  const usingFlags = flags !== undefined;
  // ONE BLOCK OF TAGS, in one place, WHERE THERE IS A SUBLINE — which is the
  // desk. Elsewhere a per-dog flag sits beside its own dog, which is right in a
  // cell with room to breathe: the chip's position IS the attribution.
  //
  // On the desk it read as two unrelated groups on one line. The names run
  // long, so the per-dog chips sat in the middle of them and the household
  // chips landed at the far right, and the eye had no reason to connect the two
  // (Sir, 2026-08-27). Gathered after the names they are one block that starts
  // where the names end, and each chip still names its dog in its title and its
  // accessible label — the attribution moves from position to words.
  const grouped = subline !== undefined;
  // WHOLE NAMES, THEN THE MARK. Only where the cell groups its flags — the desk
  // — because only there is the run of names the thing being cut.
  const { fit, boxRef, probeRef } = useFittedNames(dogs.length, dogs.map((d) => d.name).join('|'));
  const nameRun = grouped ? dogs.slice(0, fit) : dogs;
  const nameOverflow = grouped && fit < dogs.length;
  const perDog = (id: string) =>
    grouped ? [] : (flags ?? []).filter((f) => f.scope === 'dog' && f.dogId === id);
  const household = grouped
    ? // SORTED, because a ceiling makes the order load-bearing: which two chips
      // survive at a 13" width is which two matter. See DESK_FLAG_PRIORITY.
      [...(flags ?? [])].sort(byDeskPriority)
    : (flags ?? []).filter((f) => f.scope === 'household');
  return (
    <div className="bt-dogs">
      <div className="dog-pile">
        {person ? (
          <Avatar name={person.name} id={person.name} size={26} kind="person" />
        ) : (
          <>
            {shown.map((d) => (
              <Avatar key={d.id} name={d.name} id={d.id} size={26} kind="dog" />
            ))}
            {/* THE COUNTER IS AN AVATAR, not a badge of its own: same 26px
                squircle, same border, same ring against the surface, so it sits
                in the pile as a member rather than as an annotation on it. The
                names beside it already say who they are, so it is decorative —
                out of the accessibility tree. */}
            {/* STANDING ALONE, THE COUNT IS AN AVATAR — the shared one, so it
                keeps the squircle, the tone hashed off the first dog's id and
                the paw badge, and reads as a member of the pile rather than an
                annotation on it. Only its content differs: the number of dogs
                where the initials would be (Sir, 2026-08-27). */}
            {counting && (
              <span title={dogs.map((d) => d.name).join(', ')} className="avatar-count">
                {/* THROUGH `initials`, NOT `name`. Avatar takes the first letter
                    of each word, so a household of ten rendered "1" and
                    understated itself by nine dogs (codex, PR #172). */}
                <Avatar
                  name={String(dogs.length)}
                  id={dogs[0]!.id}
                  size={26}
                  kind="dog"
                  initials={String(dogs.length)}
                />
              </span>
            )}
            {/* BEHIND FACES it is a REMAINDER — "+2" after one avatar — but it
                is still a member of the pile and wears the pile's own colours
                and paw, not a grey chip pretending not to be there (Sir,
                2026-08-27). Only its two letters differ. */}
            {!counting && hidden > 0 && (
              <span
                className="avatar-count"
                title={dogs.slice(shown.length).map((d) => d.name).join(', ')}
              >
                <Avatar
                  name={dogs[shown.length]!.name}
                  id={dogs[shown.length]!.id}
                  size={26}
                  kind="dog"
                  initials={`+${hidden}`}
                />
              </span>
            )}
          </>
        )}
      </div>
      <div className="bt-meta">
        <div className="dog-names">
          {person ? (
            <span className="dog-names-text">{person.name}</span>
          ) : usingFlags ? (
            <span className="dog-names-text" ref={boxRef as React.RefObject<HTMLSpanElement>}>
              {nameRun.map((d, i) => (
                <span key={d.id} className="dog-name-unit">
                  {i > 0 && <span className="dog-sep"> · </span>}
                  {d.name}
                  <FlagStrip flags={perDog(d.id)} />
                </span>
              ))}
              {nameOverflow && (
                <>
                  <span className="dog-names-more" aria-hidden="true">
                    …
                  </span>
                  {/* THE WHOLE LIST, FOR A READER WHO CANNOT SEE THE CELL. The
                      only complete copy was the measuring probe, and that is
                      aria-hidden — so a screen-reader user got the fitted run
                      and an unlabelled ellipsis, and no way to learn which
                      other dogs belong to the booking (codex, PR #172). */}
                  <span className="sr-only">{dogs.map((d) => d.name).join(', ')}</span>
                </>
              )}
              {/* THE MEASURING COPY — every name, laid out and never drawn. It
                  is what lets the count be decided without the decision feeding
                  its own input; see useFittedNames. */}
              {grouped && dogs.length > 1 && (
                <span className="dog-names-probe" aria-hidden="true" ref={probeRef as React.RefObject<HTMLSpanElement>}>
                  {dogs.map((d, i) => (
                    <span key={d.id} className="dog-name-unit">
                      {i > 0 && <span className="dog-sep"> · </span>}
                      {d.name}
                    </span>
                  ))}
                </span>
              )}
            </span>
          ) : (
            <span className="dog-names-text">{dogs.map((d) => d.name).join(' · ')}</span>
          )}
          {/* THE FLAGS SIT BESIDE THE NAMES, where New has always sat and where
              the eye already looks for them. Moving them to the subline gave
              the names the whole line and cost the BREED its own — on a busy
              row the chips crowded it out entirely, and a row that shows its
              warnings and not what breed the dog is has traded the wrong thing
              (Sir, 2026-08-27). The names give way instead: they truncate a
              whole dog at a time and the first name is always whole, which is
              the behaviour Sir asked for in any case. */}
          {/* NO TIER ON THE STRIP. It measures its own room, and New and Viewing
              read as words at every width — where they will not fit, the names
              give way rather than the words (Sir, 2026-08-27). */}
          {usingFlags ? (
            <FlagStrip flags={household} fitToSpace={grouped} />
          ) : (
            issues &&
            issues.length > 0 && (
              <span
                className={'health-warn ' + (issuesSeverity(issues) ?? 'amber')}
                title={issues.map((i) => (i.dog ? i.dog.name + ' — ' : '') + i.label).join('\n')}
                aria-label={issues.map((i) => (i.dog ? i.dog.name + ' — ' : '') + i.label).join('; ')}
              >
                <Icon name="alert-triangle" size={11} />
              </span>
            )
          )}
        </div>
        {/* The owner line, or — where the caller has moved the owner elsewhere —
            whatever it put in that line's place. Never both: two sublines under
            one name is a cell with no lead. */}
        {/* THE LINE EXISTS WHENEVER THE CALLER ASKED FOR ONE, empty text or not.
            Gated on the STRING being non-empty, a booking whose dogs have no
            breed on file rendered no second line — and took the household's
            flags down with it, so New and the vaccine warning simply vanished
            from those rows. The line is also where the row's height comes from
            once it carries a chip. */}
        {!ownerName && subline !== undefined && (
          <div className="dog-sub">
            <span className="dog-sub-text">{subline}</span>
          </div>
        )}
        {ownerName && (
          <div className="owner-name">
            {/* Two elements, not one run of text. Sharing a single truncation
                unit with the breed FIRST in DOM order meant a long breed list
                ellipsised the owner away entirely — at 320 the line overflowed
                its column by 76px and what was lost was the name the desk
                actually works from. The breed yields; the owner does not. */}
            {meta && <span className="dog-meta">{meta} · </span>}
            <span className="owner-only">{ownerName}</span>
          </div>
        )}
      </div>
    </div>
  );
}
